export type DiffOperation = 'add' | 'remove' | 'replace' | (string & {})
export type AuthoringPreviewSection =
  'overview' | 'classification' | 'detection' | 'content' | 'impact' | 'pipeline' | 'visualization'

export interface AuthoringDiffEntryInput {
  op?: DiffOperation
  path?: string
  old?: unknown
  new?: unknown
  value?: unknown
}

export interface NormalizedAuthoringDiffEntry {
  op: DiffOperation
  path: string
  section: AuthoringPreviewSection | null
  field: string
  old: unknown
  new: unknown
  rawValue: unknown
}

export interface AuthoringDiffSummary {
  entries: NormalizedAuthoringDiffEntry[]
  bySection: Partial<Record<AuthoringPreviewSection, NormalizedAuthoringDiffEntry[]>>
  fieldClass: Record<string, DiffOperation>
  hasDiff: boolean
}

const SECTION_FIELDS: ReadonlyArray<{
  fields: readonly string[]
  section: AuthoringPreviewSection
}> = [
  { fields: ['enum', 'label', 'status', 'priority'], section: 'overview' },
  {
    fields: ['outcome_category', 'severity', 'confidence', 'platform'],
    section: 'classification',
  },
  {
    fields: ['detection_config', 'detection_sources', 'lookback_days', 'eligibility_config'],
    section: 'detection',
  },
  {
    fields: ['title_template', 'content_template', 'template_variables'],
    section: 'content',
  },
  { fields: ['impact_formula'], section: 'impact' },
  { fields: ['pipeline_steps', 'llm_config'], section: 'pipeline' },
  {
    fields: [
      'content_type',
      'column_definitions',
      'hidden_dimensions',
      'tab_definitions',
      'visualization_config',
    ],
    section: 'visualization',
  },
]

function pathParts(rawPath: unknown): string[] {
  if (!rawPath) return []
  return String(rawPath)
    .replace(/^\/+/, '')
    .split('/')
    .map((part) => part.replace(/~1/g, '/').replace(/~0/g, '~'))
}

export function sectionForPath(rawPath: unknown): AuthoringPreviewSection | null {
  const head = pathParts(rawPath)[0] ?? ''
  return SECTION_FIELDS.find((entry) => entry.fields.includes(head))?.section ?? null
}

function normalizeOperation(entry: AuthoringDiffEntryInput): DiffOperation {
  if (entry.op) return entry.op
  const hasOld = Object.prototype.hasOwnProperty.call(entry, 'old')
  const hasNew = Object.prototype.hasOwnProperty.call(entry, 'new')
  if (hasOld && hasNew) return 'replace'
  if (!hasOld && hasNew) return 'add'
  if (hasOld && !hasNew) return 'remove'
  return 'replace'
}

function normalizeEntry(entry: AuthoringDiffEntryInput): NormalizedAuthoringDiffEntry {
  const path = entry.path ?? ''
  return {
    op: normalizeOperation(entry),
    path,
    section: sectionForPath(path),
    field: pathParts(path)[0] ?? '',
    old: entry.old,
    new: Object.prototype.hasOwnProperty.call(entry, 'new') ? entry.new : entry.value,
    rawValue: entry.value,
  }
}

export function summarizeDiff(diff: unknown): AuthoringDiffSummary {
  if (!Array.isArray(diff) || diff.length === 0) {
    return { entries: [], bySection: {}, fieldClass: {}, hasDiff: false }
  }
  const entries = diff.map((entry) =>
    normalizeEntry(
      typeof entry === 'object' && entry !== null ? (entry as AuthoringDiffEntryInput) : {},
    ),
  )
  const bySection: AuthoringDiffSummary['bySection'] = {}
  const fieldClass: Record<string, DiffOperation> = {}
  for (const entry of entries) {
    if (entry.section) (bySection[entry.section] ??= []).push(entry)
    if (entry.field) fieldClass[entry.field] = entry.op
  }
  return { entries, bySection, fieldClass, hasDiff: true }
}

export function diffClassFor(
  field: string,
  fieldClass: Record<string, DiffOperation> = {},
): string {
  const operation = fieldClass[field]
  if (operation === 'add') return 'diff-added'
  if (operation === 'remove') return 'diff-removed'
  if (operation === 'replace') return 'diff-changed'
  return ''
}

export function formatDiffValue(value: unknown): string {
  if (value === undefined) return '—'
  if (value === null) return 'null'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
